import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { employmentTypeLabel } from "@/lib/constants";
import PublicNav from "@/components/PublicNav";
import { Building2, MapPin, Globe2, Users2 } from "lucide-react";

export default function CompanyPublic() {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/companies/${companyId}`)
      .then((r) => setCompany(r.data))
      .catch(() => setNotFound(true));
  }, [companyId]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicNav />
        <div data-testid="company-not-found" className="max-w-3xl mx-auto px-6 py-24 text-center text-slate-500">
          Entreprise introuvable.{" "}
          <Link to="/jobs" className="text-brand-turquoise font-medium hover:underline">Voir toutes les offres</Link>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicNav />
        <div className="max-w-5xl mx-auto px-6 py-24 flex justify-center">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-brand-turquoise rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="company-public-page" className="min-h-screen bg-slate-50">
      <PublicNav />
      <div className="bg-brand-navy">
        <div className="max-w-5xl mx-auto px-6 py-14">
          <p className="text-brand-turquoise text-xs font-bold tracking-widest uppercase mb-3">Entreprise</p>
          <h1 data-testid="company-name" className="text-3xl sm:text-4xl font-extrabold text-white mb-3">{company.name}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-white/70">
            {company.sector && <span className="flex items-center gap-1.5"><Building2 className="w-4 h-4" />{company.sector}</span>}
            {(company.city || company.country) && (
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{[company.city, company.country].filter(Boolean).join(", ")}</span>
            )}
            {company.size && <span className="flex items-center gap-1.5"><Users2 className="w-4 h-4" />{company.size} employés</span>}
            {company.website && (
              <a data-testid="company-website" href={company.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-brand-turquoise hover:underline">
                <Globe2 className="w-4 h-4" />Site web
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10 space-y-10">
        {company.description && (
          <div className="bg-white rounded-xl border border-slate-200 p-8">
            <h2 className="font-semibold text-brand-navy mb-3">À propos</h2>
            <p data-testid="company-description" className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">{company.description}</p>
          </div>
        )}

        <div>
          <h2 className="font-bold text-brand-navy text-lg mb-4">
            Offres en cours ({company.offers?.length || 0})
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {(company.offers || []).map((o) => (
              <Link
                key={o.offer_id}
                to={`/jobs/${o.offer_id}`}
                data-testid={`company-offer-${o.offer_id}`}
                className="bg-white rounded-xl border border-slate-200 p-5 hover:border-brand-turquoise hover:shadow-md transition-all duration-200"
              >
                <p className="font-semibold text-brand-navy mb-1">{o.title}</p>
                <p className="text-xs text-slate-400">
                  {[o.city, o.country].filter(Boolean).join(", ")} · {employmentTypeLabel(o.employment_type)}
                </p>
              </Link>
            ))}
            {(company.offers || []).length === 0 && (
              <p data-testid="company-offers-empty" className="text-sm text-slate-400 bg-white rounded-xl border border-slate-200 p-8 text-center md:col-span-2">
                Aucune offre active pour le moment.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
